import React, { useMemo, useState, useEffect } from 'react'
import { useOnlineStatus } from '../../contexts/OnlineStatusContext'
import defaultAvatarImage from '../../assets/images/avatars/default.png'
import './Avatar.css'

const Avatar = ({
  user,
  src,
  size = 40,
  userId,
  preview = false,
  onError,
  className,
  showCareerStatus = true,
  // Permettent de calculer les initiales quand on n'a pas d'objet `user`
  // complet (ex. <Avatar src=... firstName=... lastName=.../>).
  firstName,
  lastName,
  name,
}) => {
  const { isUserOnline } = useOnlineStatus()
  const [imgError, setImgError] = useState(false)
  const avatarUrl = user?.avatarUrl || src
  const finalUserId = user?.id || userId

  // ── Fallback INITIALES (au lieu de la silhouette default.png) ───────────────
  // Calculé depuis user (prénom/nom, sinon name, sinon email) ou les props
  // directes. Vide si rien d'exploitable → on retombe alors sur default.png
  // (zéro régression pour les appelants sans nom : src seul, etc.).
  const initials = useMemo(() => {
    const fn = String(user?.firstName ?? firstName ?? '').trim()
    const ln = String(user?.lastName ?? lastName ?? '').trim()
    if (fn || ln) {
      const a = fn.charAt(0)
      const b = ln.charAt(0)
      return (a + b || a || b).toUpperCase()
    }
    const nm = String(user?.name ?? user?.fullName ?? name ?? '').trim()
    if (nm) {
      const parts = nm.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    }
    const email = String(user?.email ?? '').trim()
    if (email) return email.charAt(0).toUpperCase()
    return ''
  }, [user, firstName, lastName, name])

  // Couleur de fond déterministe (même personne → toujours la même couleur).
  // Palette sobre et lisible (texte blanc dessus). Distinct de l'anneau
  // career_status (vert) qui reste géré séparément.
  const initialsColor = useMemo(() => {
    const key =
      `${user?.id ?? finalUserId ?? ''}|${user?.firstName ?? firstName ?? ''}|${user?.lastName ?? lastName ?? ''}|${user?.name ?? name ?? ''}` ||
      'x'
    let hash = 0
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash)
    const palette = [
      '#4f46e5', '#0891b2', '#0d9488', '#16a34a', '#ca8a04', '#ea580c',
      '#dc2626', '#db2777', '#7c3aed', '#2563eb', '#059669', '#d97706',
      '#9333ea', '#0284c7',
    ]
    return palette[Math.abs(hash) % palette.length]
  }, [user, finalUserId, firstName, lastName, name])

  // Nom affichable unifié (inclut fullName, contrairement à user?.name seul).
  const displayName =
    user?.name ||
    user?.fullName ||
    name ||
    `${firstName || ''} ${lastName || ''}`.trim() ||
    'Avatar'

  // Une URL pointant sur la silhouette default.png (asset bundlé hashé OU chemin
  // explicite passé par un appelant) ne compte PAS comme vraie photo → on
  // préfère alors les initiales.
  const isDefaultSrc =
    typeof avatarUrl === 'string' &&
    (avatarUrl === defaultAvatarImage ||
      /(^|\/)default(\.[a-z0-9]+)?\.png(\?|$)/i.test(avatarUrl))
  const hasRealImage = !!avatarUrl && !isDefaultSrc && !imgError

  // Sprint 1.5 design refresh : ring vert autour de l'avatar si le membre
  // est en recherche active ET n'a pas masqué career_status dans privacy.
  // Le ring est rendu via box-shadow (n'altère pas le layout/dimensions
  // contrairement à border). Coexiste avec le dot online/offline en
  // bas-droite (sémantiquement différent : présence vs intention carrière).
  // Pas affiché en mode preview (fullscreen) où le contexte n'est pas
  // un "scan visuel de membres".
  const showCareerRing =
    showCareerStatus
    && !preview
    && user?.career_status === 'actively_looking'
    && user?.privacy_settings?.career_status !== false

  // Réinitialiser l'erreur d'image quand l'URL change
  useEffect(() => {
    if (user?.avatarUrl || src) {
      setImgError(false)
    }
  }, [user?.avatarUrl, src])

  const handleImageError = (e) => {
    setImgError(true)
    if (onError) onError()
  }

  // Déterminer la source finale de l'image
  const finalSrc = useMemo(() => {
    if (imgError) {
      return defaultAvatarImage
    }

    if (!avatarUrl) {
      return defaultAvatarImage
    }

    // Nettoyer l'URL
    let cleanUrl = avatarUrl

    // Retirer localhost:5000 s'il est présent dans l'URL
    cleanUrl = cleanUrl.replace('http://localhost:5000', '')
    cleanUrl = cleanUrl.replace('https://localhost:5000', '')

    // Pour les chemins relatifs, utiliser l'origine actuelle
    if (!cleanUrl.startsWith('http')) {
      const origin = window.location.origin
      const avatarPath = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`
      return `${origin}${avatarPath}`
    }

    return cleanUrl
  }, [avatarUrl, imgError])

  // Configurer les dimensions
  const dimensions = useMemo(() => {
    if (preview) {
      return {
        wrapper: {
          width: '100%',
          height: 'calc(100vh - 120px)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#000',
        },
        image: {
          height: '100%',
          width: 'auto',
          maxWidth: '100%',
          objectFit: 'contain',
        },
        status: {
          size: 24,
          border: 3,
          right: 32,
          bottom: 32,
        },
      }
    }

    return {
      wrapper: {
        position: 'relative',
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        // Override le `overflow: hidden` posé par .avatar-wrapper dans
        // Avatar.css uniquement si on a un ring career-active à afficher.
        // Sans ça, le box-shadow 3px du ring est coupé par le wrapper et
        // ne s'affiche jamais. Le crop circulaire de l'image elle-même
        // reste garanti par borderRadius:50% + objectFit:cover ci-dessous.
        ...(showCareerRing ? { overflow: 'visible' } : {}),
      },
      image: {
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        objectFit: 'cover',
        backgroundColor: '#f0f0f0',
      },
      status: {
        size: Math.max(10, size * 0.25),
        border: 2,
        right: -2,
        bottom: -2,
      },
    }
  }, [size, preview, showCareerRing])

  // Vérifier le statut en ligne de l'utilisateur
  const isOnline = finalUserId ? isUserOnline(Number(finalUserId)) : false

  // Configurer le style de l'indicateur de statut
  const statusIndicatorStyles = useMemo(
    () => ({
      position: 'absolute',
      width: `${dimensions.status.size}px`,
      height: `${dimensions.status.size}px`,
      right: dimensions.status.right,
      bottom: dimensions.status.bottom,
      backgroundColor: isOnline ? '#2eb85c' : '#ced2d8',
      borderRadius: '50%',
      border: `${dimensions.status.border}px solid #fff`,
      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
      transition: 'background-color 0.3s ease',
      zIndex: 2,
    }),
    [dimensions.status, isOnline],
  )

  // Styles image : ajout d'un box-shadow ring (couleur success CoreUI) si
  // le membre est actively_looking. Pas de border car ça modifierait les
  // dimensions du wrapper et casserait les layouts qui présupposent un
  // avatar de `size` px.
  const imageStyles = useMemo(() => {
    if (!showCareerRing) return dimensions.image
    return {
      ...dimensions.image,
      boxShadow: '0 0 0 3px var(--cui-success, #2eb85c)',
    }
  }, [dimensions.image, showCareerRing])

  // Style du cercle d'initiales (mode normal). Même empreinte que l'image
  // (size px, rond) + ring career_status si applicable.
  const initialsStyles = useMemo(() => {
    const base = {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: initialsColor,
      color: '#fff',
      fontWeight: 600,
      fontSize: `${Math.max(10, Math.round(size * 0.4))}px`,
      lineHeight: 1,
      letterSpacing: '0.5px',
      userSelect: 'none',
    }
    if (showCareerRing) base.boxShadow = '0 0 0 3px var(--cui-success, #2eb85c)'
    return base
  }, [initialsColor, size, showCareerRing])

  // Pas d'initiales pour les comptes bot : ils conservent leur visuel dédié
  // (.bot-avatar img bordure warning). Ils ont de toute façon un logo.
  const showInitials = !hasRealImage && initials !== '' && user?.role !== 'bot'

  return (
    <div
      className={`avatar-wrapper ${className || ''}`}
      style={dimensions.wrapper}
      title={showCareerRing ? 'Ce membre est ouvert à être contacté' : undefined}
    >
      {preview && showInitials ? (
        // Fullscreen sans photo : grand cercle d'initiales centré (vs default.png).
        <div
          style={{
            width: 'min(45vw, 220px)',
            height: 'min(45vw, 220px)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: initialsColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: 'clamp(48px, 12vw, 96px)',
            letterSpacing: '1px',
            userSelect: 'none',
          }}
        >
          {initials}
        </div>
      ) : !preview && showInitials ? (
        // Mode normal sans photo : cercle d'initiales coloré (fallback sobre).
        <div
          style={initialsStyles}
          className={`avatar-image avatar-initials${showCareerRing ? ' avatar-image--career-active' : ''}`}
          aria-label={displayName}
        >
          {initials}
        </div>
      ) : (
        <img
          src={finalSrc}
          alt={displayName}
          style={imageStyles}
          onError={handleImageError}
          className={`avatar-image${showCareerRing ? ' avatar-image--career-active' : ''}`}
          key={`${finalSrc}`}
          crossOrigin="anonymous"
        />
      )}
      {finalUserId && (
        <span
          style={statusIndicatorStyles}
          title={isOnline ? 'En ligne' : 'Hors ligne'}
          className="status-indicator"
        />
      )}
    </div>
  )
}

export default Avatar
